import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Grid,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Search,
  AccessTime,
  Person,
  Star,
  Visibility,
  ShoppingCart,
  ShoppingCartCheckout,
  Category,
} from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { getCourses, searchApprovedCourses } from "../../State/Course/Action";
import { getCourseTypes } from "../../State/Enum/Action";
import AppPagination from "../Tools/AppPagination";
import { useNavigate } from "react-router-dom";
import {
  addCourseToCart,
  getCoursesFromCart,
  resetAddCourseToCart,
} from "../../State/Cart/Action";
import SuccessDialog from "../Tools/SuccessDialog";
import FailureDialog from "../Tools/FailureDialog";
import CourseReview from "./CourseReviews";
import { motion } from "framer-motion";

// Helper function to convert uppercase/caps to title case
const toTitleCase = (str) => {
  if (!str) return str;
  // Split by underscore or space first
  return str
    .split(/[_\s]+/)
    .map((word) => {
      if (!word) return word;
      // If word is all uppercase, convert to title case
      if (word === word.toUpperCase()) {
        return word.charAt(0) + word.slice(1).toLowerCase();
      }
      // Otherwise, just capitalize first letter
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
};

const BrowseCourses = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [category, setCategory] = useState("");
  const [page, setPage] = useState(0);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showFailureDialog, setShowFailureDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [openReviewDialog, setOpenReviewDialog] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState(null);

  const totalPages = useSelector(
    (store) => store.course.courseListResponse?.totalPages || 0
  );
  const courses = useSelector(
    (store) => store.course.courseListResponse?.courses || []
  );

  const addCourseToCartResult = useSelector(
    (store) => store.cart.addCourseToCartResult
  );

  const courseTypes = useSelector(
    (store) => store.enums.courseTypes || []
  );

  const dispatch = useDispatch();

  const cartCourseIds = useSelector(
    (state) => Array.isArray(state.cart?.courseIds) ? state.cart.courseIds : []
  );
  const isCourseInCart = (courseId) => cartCourseIds.includes(courseId);

  const handleAddToCart = (courseId) => {
    dispatch(addCourseToCart(courseId));
  };

  const navigate = useNavigate();
  useEffect(() => {
    dispatch(resetAddCourseToCart());
    dispatch(getCoursesFromCart());
    dispatch(getCourseTypes());
  }, [dispatch]);

  useEffect(() => {
    // If either search term or category present, use search endpoint
    if ((searchTerm && searchTerm.trim() !== "") || (category && category !== "")) {
      dispatch(searchApprovedCourses(searchTerm.trim(), page, category));
    } else {
      dispatch(getCourses(page, "APPROVED"));
    }
  }, [searchTerm, category, page, dispatch]);

  useEffect(() => {
    if (addCourseToCartResult?.success) {
      setDialogMessage(
        addCourseToCartResult.message || "Course added to cart successfully."
      );
      setShowSuccessDialog(true);
    } else if (addCourseToCartResult?.success === false) {
      setDialogMessage(
        addCourseToCartResult.message || "Failed to add course to cart."
      );
      setShowFailureDialog(true);
    }
  }, [addCourseToCartResult]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(0); // Reset to first page when search term changes
  };

  const handleCategoryChange = (e) => {
    setCategory(e.target.value);
    setPage(0); // Reset to first page when category changes
  };

  const handleOpenReviewDialog = (courseId) => {
    setSelectedCourseId(courseId);
    setOpenReviewDialog(true);
  };

  const handleCloseReviewDialog = () => {
    setOpenReviewDialog(false);
    setSelectedCourseId(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-r from-primary-200 to-secondary-200 rounded-full mix-blend-multiply filter blur-xl opacity-50 animate-float"></div>
        <div className="absolute bottom-20 right-10 w-72 h-72 bg-gradient-to-r from-secondary-200 to-accent-200 rounded-full mix-blend-multiply filter blur-xl opacity-50 animate-float" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="relative p-6 max-w-7xl mx-auto">
        <SuccessDialog
          open={showSuccessDialog}
          onClose={() => {
            setShowSuccessDialog(false);
            dispatch(resetAddCourseToCart());
            dispatch(getCoursesFromCart());
          }}
          message={dialogMessage}
        />
        <FailureDialog
          open={showFailureDialog}
          onClose={() => {
            setShowFailureDialog(false);
            dispatch(resetAddCourseToCart());
          }}
          message={dialogMessage}
        />

        <Dialog
          open={openReviewDialog}
          onClose={handleCloseReviewDialog}
          fullWidth
          maxWidth="md"
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            }
          }}
        >
          <DialogTitle sx={{ 
            background: 'linear-gradient(135deg, #0ea5e9 0%, #a855f7 100%)',
            color: 'white',
            borderRadius: '12px 12px 0 0',
            textAlign: 'center',
            py: 3
          }}>
            Course Reviews
          </DialogTitle>
          <DialogContent sx={{ p: 4 }}>
            {selectedCourseId && <CourseReview courseId={selectedCourseId} />}
          </DialogContent>
        </Dialog>

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-gray-900 via-primary-700 to-secondary-700 bg-clip-text text-transparent mb-4">
            Discover Amazing Courses
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Explore our curated collection of expert-led courses designed to accelerate your learning journey
          </p>
        </div>

        {/* Search and Filter */}
        <div className="glass p-6 rounded-2xl shadow-large border border-white/20 mb-8">
          <div className="flex flex-col md:flex-row gap-4 justify-center items-center">
            <TextField
              variant="outlined"
              placeholder="Search by course name..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: '#0ea5e9' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1,
                width: '100%',
                minWidth: { xs: '100%', sm: 200, md: 250 },
                maxWidth: 400,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                  },
                },
              }}
            />
            <FormControl
              sx={{
                flex: 1,
                width: '100%',
                minWidth: { xs: '100%', sm: 200, md: 250 },
                maxWidth: 400,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                  },
                },
              }}
            >
              <InputLabel id="category-select-label" sx={{ 
                '&.MuiInputLabel-shrink': {
                  transform: 'translate(14px, -9px) scale(0.75)',
                }
              }}>Filter by category</InputLabel>
              <Select
                labelId="category-select-label"
                value={category}
                label="Filter by category"
                onChange={handleCategoryChange}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      minWidth: 250,
                      maxHeight: 400,
                    }
                  }
                }}
                sx={{
                  borderRadius: '12px',
                  minWidth: { xs: '100%', sm: 200, md: 250 },
                  width: '100%',
                  '& .MuiSelect-select': {
                    padding: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    minWidth: 0,
                  },
                }}
              >
                <MenuItem value="" sx={{ whiteSpace: 'nowrap' }}>
                  <em>All Categories</em>
                </MenuItem>
                {courseTypes.map((type) => (
                  <MenuItem key={type} value={type} sx={{ whiteSpace: 'nowrap', minWidth: 200 }}>
                    {toTitleCase(type)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </div>

        {/* Courses Grid */}
        {courses.length === 0 ? (
          <div className="text-center py-16">
            <div className="glass p-8 rounded-2xl shadow-medium border border-white/20 max-w-2xl mx-auto">
              <Search sx={{ fontSize: 64, color: '#9ca3af', mb: 3 }} />
              <Typography variant="h5" sx={{ color: '#1f2937', fontWeight: 600, mb: 2 }}>
                No courses found
              </Typography>
              <Typography variant="body1" sx={{ color: '#6b7280', mb: 3 }}>
                {searchTerm || category
                  ? "We couldn't find any courses matching your search criteria. Try adjusting your filters or search terms."
                  : "There are no approved courses available at the moment. Please check back later."}
              </Typography>
              {(searchTerm || category) && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    setSearchTerm("");
                    setCategory("");
                    setPage(0);
                  }}
                  sx={{
                    borderRadius: '12px',
                    textTransform: 'none',
                    borderColor: '#0ea5e9',
                    color: '#0ea5e9',
                    '&:hover': {
                      borderColor: '#0284c7',
                      backgroundColor: 'rgba(14, 165, 233, 0.1)',
                    },
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </div>
        ) : (
        <Grid container spacing={4}>
          {courses.map((course, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={course.id}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                whileHover={{ y: -8, scale: 1.02 }}
                className="h-full"
              >
                <Card
                  sx={{
                    height: "100%",
                    borderRadius: 4,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    transition: "all 0.3s ease",
                    overflow: 'hidden',
                    "&:hover": {
                      boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                      transform: 'translateY(-8px) scale(1.02)',
                    },
                  }}
                >
                  <div className="relative overflow-hidden">
                    <CardMedia
                      component="img"
                      image={course.thumbnailUrl}
                      onClick={() => navigate(`/course-complete-detail/${course.id}`)}
                      alt={course.name}
                      sx={{
                        height: 200,
                        objectFit: "cover",
                        cursor: 'pointer',
                        transition: 'transform 0.3s ease',
                        '&:hover': {
                          transform: 'scale(1.05)',
                        },
                      }}
                    />
                    <div className="absolute top-4 right-4">
                      <div className="bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full shadow-soft">
                        <div className="flex items-center gap-1">
                          <Star sx={{ color: "#FFD700", fontSize: 16 }} />
                          <span className="text-sm font-semibold text-gray-700">
                            {course.averageRating.toFixed(1)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <CardContent sx={{ p: 3 }}>
                    <div className="flex items-start justify-between mb-2">
                      <Typography 
                        variant="h6" 
                        fontWeight="bold" 
                        className="line-clamp-2 flex-1"
                        sx={{ 
                          fontSize: '1.1rem',
                          lineHeight: 1.3,
                          color: '#1f2937'
                        }}
                      >
                        {course.name}
                      </Typography>
                      {course.type && (
                        <Chip
                          icon={<Category sx={{ fontSize: 16 }} />}
                          label={toTitleCase(course.type)}
                          size="small"
                          sx={{
                            ml: 1,
                            fontSize: '0.7rem',
                            height: '24px',
                            backgroundColor: 'rgba(14, 165, 233, 0.1)',
                            color: '#0ea5e9',
                            fontWeight: 600,
                            '& .MuiChip-icon': {
                              color: '#0ea5e9',
                            },
                          }}
                        />
                      )}
                    </div>

                    <Typography
                      variant="body2"
                      sx={{ 
                        color: '#6b7280', 
                        mb: 2,
                        lineHeight: 1.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {course.description?.length > 100
                        ? `${course.description.slice(0, 100)}...`
                        : course.description}
                    </Typography>

                    {/* Course Meta */}
                    <div className="flex items-center justify-between mb-3 text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <AccessTime fontSize="small" />
                        <span>{course.length || 'N/A'}</span>
                      </div>
                      <div className="flex items-center gap-1 cursor-pointer hover:text-primary-600 transition-colors"
                           onClick={() => handleOpenReviewDialog(course.id)}>
                        <span>({course.totalReviews} reviews)</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 mb-3 text-sm text-gray-600">
                      <Person fontSize="small" />
                      <span className="font-medium">{course.instructor}</span>
                    </div>

                    {/* Pricing */}
                    <div className="flex items-center gap-2 mb-4">
                      <Typography
                        variant="body2"
                        sx={{ 
                          textDecoration: "line-through",
                          color: '#9ca3af',
                          fontSize: '0.9rem'
                        }}
                      >
                        ₹{course.price}
                      </Typography>
                      <span className="bg-red-100 text-red-600 px-2 py-1 rounded-md text-xs font-semibold">
                        -₹{course.discountAmount} Off
                      </span>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          color: '#0ea5e9',
                          fontWeight: 'bold',
                          fontSize: '1.2rem'
                        }}
                      >
                        ₹{course.finalPrice}
                      </Typography>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <Tooltip
                        title={
                          course.subscribed
                            ? "You already have access to this course"
                            : isCourseInCart(course.id)
                            ? "Added to Cart"
                            : "Add to Cart"
                        }
                      >
                        <Button
                          variant="contained"
                          fullWidth
                          onClick={() => {
                            if (!isCourseInCart(course.id) && !course.subscribed) {
                              handleAddToCart(course.id);
                            }
                          }}
                          disabled={isCourseInCart(course.id) || course.subscribed}
                          startIcon={
                            isCourseInCart(course.id) ? (
                              <ShoppingCartCheckout />
                            ) : (
                              <ShoppingCart />
                            )
                          }
                          sx={{
                            borderRadius: '12px',
                            py: 1.5,
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            background: isCourseInCart(course.id) 
                              ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                              : course.subscribed
                              ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
                              : 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                            '&:hover': {
                              background: isCourseInCart(course.id)
                                ? 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                                : course.subscribed
                                ? 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)'
                                : 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                              transform: 'translateY(-1px)',
                            },
                            '&:disabled': {
                              background: '#e5e7eb',
                              color: '#9ca3af',
                            }
                          }}
                        >
                          {isCourseInCart(course.id)
                            ? "In Cart"
                            : course.subscribed
                            ? "Subscribed"
                            : "Add to Cart"}
                        </Button>
                      </Tooltip>

                      <Tooltip title="View Details">
                        <IconButton
                          onClick={() =>
                            navigate(`/course-complete-detail/${course.id}`)
                          }
                          sx={{
                            borderRadius: '12px',
                            backgroundColor: 'rgba(14, 165, 233, 0.1)',
                            color: '#0ea5e9',
                            '&:hover': {
                              backgroundColor: 'rgba(14, 165, 233, 0.2)',
                              transform: 'scale(1.05)',
                            },
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
        )}

        {/* Pagination - Only show if there are courses */}
        {courses.length > 0 && totalPages > 0 && (
          <div className="flex justify-center mt-12">
            <div className="glass p-4 rounded-2xl shadow-medium border border-white/20">
              <AppPagination setPage={setPage} totalPages={totalPages} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BrowseCourses;
